/**
 * What is still missing, said before it costs anything (#2469).
 *
 * A workshop used to find out at the counter: the receipt refused, or the
 * invoice would not finalize. Both errors point at Settings, so this is where
 * the answer belongs — and each gap is a button, because naming a missing
 * field without saying where to fix it only moves the search.
 *
 * Silent when nothing is missing. A green "all good" banner would be true
 * almost always and therefore read as decoration, and it would push the
 * settings themselves further down the page for no gain.
 */

import { Alert, AlertTitle, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { WorkshopSettings } from '@/types';
import { useWorkshopReadiness } from '@/features/settings/useWorkshopReadiness';
import type { ReadinessSection } from '@/features/settings/workshopReadiness';
import type { SettingsTab } from '@/features/settings/settingsTabs';

interface Props {
  settings: WorkshopSettings;
  /** The workshop page this alert is sitting on (#2428). */
  currentTab: SettingsTab;
  /** Scrolls the named section into view; the page owns the anchors. */
  onJumpToSection: (section: ReadinessSection) => void;
}

export function WorkshopReadinessAlert({ settings, currentTab, onJumpToSection }: Props) {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { issues, blocking, isLoading } = useWorkshopReadiness(settings);

  // Stay quiet until the counts have arrived: announcing "no mechanics" while
  // the query is still in flight would be wrong more often than right.
  if (isLoading || issues.length === 0) return null;

  return (
    <Alert severity={blocking ? 'error' : 'warning'} sx={{ mb: 3 }}>
      <AlertTitle>
        {blocking ? t('readiness.blockingTitle') : t('readiness.warningTitle')}
      </AlertTitle>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
        {issues.map((issue) => (
          <Button
            key={issue.key}
            size="small"
            variant="text"
            color="inherit"
            onClick={() => {
              if (issue.target.tab === currentTab && issue.target.section) {
                onJumpToSection(issue.target.section);
              } else {
                // Another tab entirely — pricing, hours, services, lifts and
                // staff are not on this form, so scrolling cannot reach them.
                navigate(`/settings?tab=${issue.target.tab}`);
              }
            }}
            sx={{ textAlign: 'left', textTransform: 'none', p: 0, minWidth: 0 }}
          >
            {t(`readiness.issue.${issue.key}`)}
          </Button>
        ))}
      </Box>
    </Alert>
  );
}
